import { useEffect, useState } from "react";

import { MedicamentoCombobox } from "@/components/MedicamentoCombobox";
import { RoleGate } from "@/components/RoleGate";
import { useComposicao, useMedicamentos, useSalvarComposicao } from "@/hooks/useApi";

const TIPOS = [
  { value: "caixa_anestesia", label: "Caixa de Anestesia" },
  { value: "caixa_psicotropicos", label: "Caixa de Psicotrópicos (C.Cir.)" },
  { value: "carrinho_parada", label: "Carrinho de Parada" },
];

interface Linha {
  key: string;
  medicamento_id: string;
  qtd_alvo: string;
}

export function ComposicaoKitsPage() {
  return (
    <RoleGate allowed={["admin", "farmaceutico"]}>
      <Inner />
    </RoleGate>
  );
}

function Inner() {
  const [tipo, setTipo] = useState("caixa_anestesia");
  const { data: composicao, isLoading } = useComposicao(tipo);
  const { data: medicamentos } = useMedicamentos({});
  const salvar = useSalvarComposicao();
  const [linhas, setLinhas] = useState<Linha[]>([]);

  // Carrega composição existente quando troca de tipo
  useEffect(() => {
    if (!composicao) return;
    setLinhas(
      composicao.length > 0
        ? composicao.map((c) => ({ key: crypto.randomUUID(), medicamento_id: c.medicamento_id, qtd_alvo: String(c.qtd_alvo) }))
        : [{ key: crypto.randomUUID(), medicamento_id: "", qtd_alvo: "1" }],
    );
  }, [composicao, tipo]);

  function update(key: string, patch: Partial<Linha>) {
    setLinhas((a) => a.map((l) => (l.key === key ? { ...l, ...patch } : l)));
  }
  function add() {
    setLinhas((a) => [...a, { key: crypto.randomUUID(), medicamento_id: "", qtd_alvo: "1" }]);
  }
  function remove(key: string) {
    setLinhas((a) => a.filter((l) => l.key !== key));
  }

  async function submit() {
    const itens = linhas
      .filter((l) => l.medicamento_id && Number(l.qtd_alvo) > 0)
      .map((l) => ({ medicamento_id: l.medicamento_id, qtd_alvo: Number(l.qtd_alvo) }));
    if (!itens.length) return;
    // dedup por medicamento (mantém último)
    const map = new Map(itens.map((i) => [i.medicamento_id, i]));
    await salvar.mutateAsync({ tipoKit: tipo, itens: [...map.values()] });
  }

  return (
    <div className="space-y-5">
      <div>
        <h2 className="font-display text-2xl font-semibold">Composição de Kits</h2>
        <p className="text-sm text-ink-muted">
          Quantidade-alvo de cada medicamento por tipo de caixa (par-level). Usado pela
          reposição. Vale pras caixas do mesmo tipo (todas idênticas).
        </p>
      </div>

      <div className="flex items-center gap-3">
        <label className="text-sm text-ink-muted">Tipo de kit:</label>
        <select className="input max-w-xs" value={tipo} onChange={(e) => setTipo(e.target.value)}>
          {TIPOS.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
        </select>
      </div>

      {isLoading ? <p className="text-ink-muted">Carregando…</p> : null}

      <div className="card p-4 space-y-2">
        <div className="grid grid-cols-[1fr_120px_auto] gap-2 text-xs uppercase tracking-wider text-ink-muted">
          <span>Medicamento</span><span className="text-right">Qtd alvo</span><span></span>
        </div>
        {linhas.map((l) => (
          <div key={l.key} className="grid grid-cols-[1fr_120px_auto] gap-2">
            <MedicamentoCombobox
              medicamentos={medicamentos ?? []}
              value={l.medicamento_id}
              onChange={(id) => update(l.key, { medicamento_id: id })}
            />
            <input className="input text-right" type="number" min={1} value={l.qtd_alvo} onChange={(e) => update(l.key, { qtd_alvo: e.target.value })} />
            <button className="text-ink-muted hover:text-anarosa text-sm" onClick={() => remove(l.key)}>Remover</button>
          </div>
        ))}
        <button className="text-anarosa text-sm hover:underline" onClick={add}>+ Adicionar medicamento</button>
      </div>

      <div className="flex items-center gap-3">
        <button className="btn-primary" disabled={salvar.isPending} onClick={submit}>
          {salvar.isPending ? "Salvando…" : "Salvar composição"}
        </button>
        {salvar.error ? <span className="text-anarosa text-sm">{(salvar.error as Error).message}</span> : null}
        {salvar.isSuccess ? <span className="text-green-700 text-sm">✅ Composição salva.</span> : null}
        <span className="text-xs text-ink-muted ml-auto">{linhas.filter((l) => l.medicamento_id).length} itens</span>
      </div>
    </div>
  );
}
